import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Layers, ShieldCheck, Users, Leaf, MessageSquare, HandCoins, Star } from "lucide-react";
import { api } from "../lib/api";

export default function Landing() {
  const [jobs, setJobs] = useState([]);
  const [brand, setBrand] = useState({ site_name: "Ocean Ash Ltd", tagline: "", logo_url: "", primary_color: "#D94833" });

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data.slice(0, 6))).catch(() => {});
    api.get("/platform/branding").then((r) => setBrand(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E2E0D8] dark:border-[#333]">
        <div
          className="absolute inset-0 opacity-30 dark:opacity-15"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800)",
            backgroundSize: "cover", backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 hero-overlay dark:bg-gradient-to-b dark:from-[#121212]/20 dark:via-[#121212]/92 dark:to-[#121212]" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-28">
          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 lg:col-span-8">
              <div className="label-eyebrow mb-6 flex items-center gap-2">
                <ShieldCheck className="w-3 h-3" style={{ color: brand.primary_color }} /> {brand.site_name} — Careers
              </div>
              <h1 className="font-display font-black tracking-tight text-5xl sm:text-6xl lg:text-[88px] leading-[0.95]">
                Build the future of <span className="italic font-medium">applications</span><br />
                with <span className="text-[var(--brand-primary)]">us.</span>
              </h1>
              <p className="mt-8 text-lg lg:text-xl max-w-2xl text-[#3a3a3a] dark:text-[#bbb] leading-relaxed">
                Made for small businesses, affordable for small businesses. Fair Tiers, No Excessive 5 Year Contracts. We grow with you, not against you.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/jobs" data-testid="hero-browse-jobs" className="btn-terra text-base">
                  Browse open roles <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/register" data-testid="hero-register" className="btn-ghost text-base">
                  Create an account <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="hidden lg:flex col-span-4 flex-col items-end gap-2">
              <div className="border border-[#0F0F0F] dark:border-[#555] px-6 py-5">
                <div className="label-eyebrow">Live</div>
                <div className="font-display font-black text-5xl mt-2">{jobs.length}+</div>
                <div className="text-sm">open roles across employers</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured jobs */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-eyebrow">01 — Currently hiring</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl mt-3 tracking-tight">
              Roles on the board
            </h2>
          </div>
          <Link to="/jobs" data-testid="see-all-jobs" className="hidden sm:inline-flex items-center gap-1 font-semibold hover:text-[var(--brand-primary)]">
            See all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E0D8] dark:bg-[#333] border border-[#E2E0D8] dark:border-[#333]">
          {jobs.length === 0 && (
            <div className="bg-[#FDFCF8] dark:bg-[#1a1a1a] p-10 col-span-full text-[#5C5C5C] dark:text-[#999]">No roles yet.</div>
          )}
          {jobs.map((j) => (
            <Link
              key={j.job_id}
              to={`/jobs/${j.job_id}`}
              data-testid={`landing-job-${j.job_id}`}
              className="bg-[#FDFCF8] dark:bg-[#1a1a1a] p-8 hover:bg-[#F3F2EB] dark:hover:bg-[#252525] transition flex flex-col justify-between min-h-[220px] group relative"
            >
              {j.tier === "premium" && (
                <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold bg-[#0F0F0F] text-[#FDFCF8] px-2 py-1">
                  <Star className="w-3 h-3 text-[var(--brand-primary)]" /> Spotlight
                </span>
              )}
              {j.tier === "featured" && (
                <span className="absolute top-4 right-4 text-[10px] uppercase tracking-widest font-semibold border border-[var(--brand-primary)] text-[var(--brand-primary)] px-2 py-1">
                  Featured
                </span>
              )}
              <div>
                <div className="label-eyebrow">{j.company}</div>
                <h3 className="font-display font-bold text-2xl mt-2 leading-tight group-hover:text-[var(--brand-primary)] transition">
                  {j.title}
                </h3>
              </div>
              <div className="flex items-center justify-between text-sm mt-6">
                <span className="text-[#5C5C5C] dark:text-[#999]">{j.location} · {j.employment_type}</span>
                <ArrowUpRight className="w-5 h-5" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-[#0F0F0F] text-[#FDFCF8] border-y border-[#0F0F0F]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-5">
              <div className="label-eyebrow" style={{ color: "#a8a8a8" }}>02 — Why Ocean Ash Ltd</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl mt-3 leading-tight">
                Built for small businesses. <span className="text-[var(--brand-primary)]">Run like one.</span>
              </h2>
              <p className="text-[#c2c2c2] mt-6 leading-relaxed">
                We're a small team. We know how it feels to count every applicant, every pound and every hour. So we built the hiring tool we wanted ourselves.
              </p>
            </div>
            <div className="col-span-12 lg:col-span-7 grid sm:grid-cols-2 gap-px bg-[#FDFCF8]/10">
              {[
                { icon: Leaf, t: "Sustainability first", d: "Lean infrastructure, no creepy ad trackers, carbon-aware hosting. We measure it. We publish it." },
                { icon: MessageSquare, t: "Transparency", d: "9 honest applicant statuses — including 'On Hold' and 'Flagged'. No vague black holes." },
                { icon: Users, t: "Made for small businesses", d: "Free plan covers most teams under 10. Fair tiers above that. We grow with you, not against you." },
                { icon: HandCoins, t: "Discounts on request", d: "Charities, co-ops, family businesses, early-stage founders — get in touch. We'll work it out." },
                { icon: ShieldCheck, t: "Moderation that works", d: "Reports are reviewed by a human within 48 hours. Bad actors get blacklisted." },
                { icon: Layers, t: "Free for candidates today", d: "Candidates currently pay nothing. Recruiters keep their data on cancellation — no ransom, no lock-in. Plan terms can change with notice." },
              ].map((f, i) => (
                <div key={i} className="bg-[#0F0F0F] p-8">
                  <f.icon className="w-6 h-6 text-[var(--brand-primary)]" />
                  <h3 className="font-display font-bold text-xl mt-4">{f.t}</h3>
                  <p className="text-sm text-[#c2c2c2] mt-2 leading-relaxed">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="border border-[#0F0F0F] p-10 lg:p-16 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 lg:col-span-8">
            <div className="label-eyebrow">03 — Get started</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl mt-3 tracking-tight">
              60 seconds to a free account. <span className="text-[var(--brand-primary)]">No card required.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <Link to="/pricing" data-testid="cta-pricing" className="btn-ink text-base">See pricing</Link>
            <Link to="/register" data-testid="cta-register" className="btn-terra text-base">Create account</Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E2E0D8] dark:border-[#333] py-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 space-y-6">
          <div className="flex items-center justify-between text-sm text-[#5C5C5C] dark:text-[#999] flex-wrap gap-4">
            <span className="font-display font-bold text-[#0F0F0F] dark:text-[#FDFCF8]">Ocean Ash Ltd</span>
            <div className="flex gap-6 flex-wrap">
              <Link to="/jobs" className="hover:text-[var(--brand-primary)]">Open Roles</Link>
              <Link to="/pricing" className="hover:text-[var(--brand-primary)]">Pricing</Link>
              <Link to="/tracker" data-testid="footer-tracker-link" className="hover:text-[var(--brand-primary)]">Track Account</Link>
              <Link to="/disclaimer" data-testid="footer-disclaimer-link" className="hover:text-[var(--brand-primary)]">Disclaimer</Link>
              <a href="https://legal.oceanash.org" target="_blank" rel="noreferrer" data-testid="footer-legal-link" className="hover:text-[var(--brand-primary)]">Legal</a>
              <a href="mailto:admin@oceanash.org" className="hover:text-[var(--brand-primary)]">Contact</a>
            </div>
            <span>© {new Date().getFullYear()} Ocean Ash Ltd</span>
          </div>
          <p className="text-[11px] text-[#5C5C5C] dark:text-[#888] leading-relaxed border-t border-[#E2E0D8] dark:border-[#333] pt-6" data-testid="footer-disclaimer">
            Ocean Ash acts solely as a platform to advertise, promote, and facilitate job opportunities between employers and prospective workers. We do not employ, supervise, manage, or control the individuals or businesses offering or undertaking the work. All employers and workers are independently responsible for legal, regulatory, licensing, tax, and employment compliance. <Link className="underline hover:text-[var(--brand-primary)]" to="/disclaimer">Read the full disclaimer →</Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
